/**
 * IMPORTANT:
 * Fix the TODOs in the code below. Once done, make sure to check the external code that
 * is using each function and make sure it's working as expected.
 *
 * And make sure to update the wagercosting.service.spec.ts file on every changes.
 *
 * Note: This code was copied from PRO UI.
 */


/**
 * Compute the number of bets for a wager configuration
 * @param {string} modifier Bet modifier (box, powerbox, keybox, wheel, ...)
 * @param {any} legs Number of legs
 * @param {any} combine Combine multiplier
 * @param {any} runners Runners per leg
 * @param {any} baseAmount Base bet amount
 * @param {any} maxAmount Max allowed total
 * @param {boolean} unordered True when order doesn't matter
 * @param {any} races Number of races involved
 * @returns {number} Number of bets, or -1 on invalid input
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export const calcBets = (
  modifier: string,
  legs: any, //number --> TODO: don't use any
  combine: any, //number --> TODO: don't use any
  runners: any, // TODO: don't use any
  baseAmount: any, //number --> TODO: don't use any
  maxAmount: any, //number --> TODO: don't use any
  unordered: boolean,
  races: any //number --> TODO: don't use any
): number => {

  // return -1 if any of the following are true:
  if (
    baseAmount < 0.01 ||
    maxAmount < 0.01 ||
    runners == null ||
    runners.length < 1 ||
    legs < 1 ||
    combine < 0
  ) {
    return -1;
  }

  // calculate the max count
  const maxCount = maxAmount / baseAmount;

  let count = 0;
  let actualRunners = [];

  // if combine is undefined, set it to 1
  if (typeof combine == 'undefined') {
    combine = 1;
  }

  let ordered: any; // TODO: don't use any
  ordered = 1;

  // TODO: need explanation on this
  if (typeof unordered != 'undefined') {
    ordered = !unordered;
  }

  // execute the switch case based on the modifier
  switch (modifier.toLowerCase()) {
    case "box":
      // return -1 if the number of runners is not 1
      if (runners.length !== 1) return -1;

      // TODO: need explanation on this
      actualRunners.push(runners[0]);
      for (let i = 1; i < legs; i++) {
        actualRunners.push(runners[0]);
      }

      // calculate the number of bets
      count = countBets(actualRunners, legs, combine, maxCount, ordered);

      break;
    case "powerbox":
      // return -1 if the number of runners is not the same as the number of legs or if ordered is false
      if (runners.length !== legs || !ordered) return -1;

      // calculate the number of bets
      count = countKeyBoxBets(runners, legs, combine, true, maxCount, ordered);

      break;
    case "leading":
      // return -1 if the number of runners is not 2 or if ordered is false
      if (runners.length !== 2 || !ordered) return -1;

      actualRunners.push(runners[0]);
      actualRunners.push(runners[1]);

      // TODO: need explanation on this
      for (let j = 2; j < legs; j++) {
        actualRunners.push(runners[1]);
      }

      // calculate the number of bets
      count = countBets(actualRunners, legs, combine, maxCount, ordered);

      break;
    case "keywheel":
      // return -1 if the number of runners is not 2 or if ordered is false
      if (runners.length !== 2 || !ordered) return -1;

      // TODO: need explanation on this
      for (let k = 0; k < legs; k++) {
        actualRunners = [];

        for (let l = 0; l < legs; l++) {
          if (l === k) {
            actualRunners.push(runners[0]);
            continue;
          }

          actualRunners.push(runners[1]);
        }

        // calculate the number of bets
        count = count + countBets(actualRunners, legs, combine, maxCount, ordered);
      }

      break;
    case "keybox":
      // return -1 if the number of runners is not the same as the number of legs or if ordered is false
      if ((runners.length !== legs) || !ordered) return -1;

      // calculate the number of bets
      count = countKeyBoxBets(runners, legs, combine, false, maxCount, ordered);

      break;
    case "wheel":
      // calculate the number of bets
      count = countBets(runners, legs, combine, maxCount, ordered);

      break;
    default:
      // return -1 if the number of runners is not the same as the number of legs times the number of races
      if (runners.length != races * legs) return -1;

      // return -1 if any of the runners have no legs
      for (let m = 0; m < legs; m++) {
        if (runners[m].length < 1) return -1;
      }

      // calculate the number of bets
      count = countBets(runners, legs, combine, maxCount, ordered);
      break;
  }

  // return the number of bets
  return count;
};


/**
 * Count KeyBox/PowerBox bets across all leg permutations
 * @param {any} parsedRunners Runners per leg
 * @param {any} legs Number of legs
 * @param {any} combine Combine multiplier
 * @param {any} removeDuplicates Deduplicate identical bets when true
 * @param {any} maxCount Max allowed bets
 * @param {any} ordered True when order matters
 * @returns {number} Number of bets, or -2 when exceeding maxCount
 */
export const countKeyBoxBets = (
  parsedRunners: any, legs: any, combine: any, removeDuplicates: any, maxCount: any, ordered: any
): number => {
  let count = 0;
  let possibleLegPositions = [];

  // TODO: need explanation on this
  for (let i = 0; i < legs; i++) {
    const innerPerm = [];
    for (let j = 0; j < legs; j++) {
      innerPerm.push(j);
    }
    possibleLegPositions.push(innerPerm);
  }

  // get the permutations of the possible leg positions
  possibleLegPositions = getPerms(possibleLegPositions, [], 0, null, maxCount);

  // return -2 if possibleLegPositions is null
  if (possibleLegPositions === null) return -2;

  // eslint-disable-next-line prefer-const
  let uniqueBets: any = {}; // TODO: don't use any
  // uniqueBets = {};

  // loop through the possible leg positions
  for (let k = 0; k < possibleLegPositions.length; k++) {

    // TODO: need explanation on this
    if (count < 0) return count;

    const actualRunners = [];
    // get actualRunners from the possible leg positions
    for (let l = 0; l < possibleLegPositions[k].length; l++) {
      actualRunners.push(parsedRunners[possibleLegPositions[k][l]]);
    }

    // get uniqueBets
    if (removeDuplicates) {
      let betPermRunners;

      // get the permutations of the actual runners
      const betPerms = getPerms(actualRunners, [], 0, null, maxCount);

      // return -2 if betPerms is null
      if (betPerms === null) return -2;

      // loop through the permutations to get betPermRunners
      for (let m = 0; m < betPerms.length; m++) {
        betPermRunners = betPerms[m][0];
        for (let n = 1; n < betPerms[m].length; n++) {
          betPermRunners = betPermRunners + "," + betPerms[m][n];
        }

        // add the betPermRunners to the uniqueBets
        uniqueBets[betPermRunners] = true;
      }
    }
    else {
      // calculate the number of bets
      count = count + countBets(actualRunners, legs, combine, maxCount, ordered);
    }
  }

  // if removeDuplicates is true, return the number of unique bets
  if (removeDuplicates) {
    count = Object.keys(uniqueBets).length;
  }

  return count;
};


/**
 * Count bets (ordered permutations or unordered combos)
 * @param {any} runners Runners per leg
 * @param {any} legs Number of legs
 * @param {any} combine Combine multiplier
 * @param {any} maxCount Max allowed bets
 * @param {any} ordered True when order matters
 * @returns {number} Number of bets (multiplied by combine)
 */
export const countBets = (runners: any, legs: any, combine: any, maxCount: any, ordered: any) => {
  let count;

  if (ordered) {
    // calculate the number of bets
    count = legs === 1 ? countSingle(runners, maxCount) : countPerms(runners, [], 0, 0, maxCount);
  } else {
    // calculate the number of bets
    count = countCombos(runners, maxCount);
  }

  // return the number of bets
  return count < 0 ? count : (combine < 1 ? 1 : combine) * count;
};


/**
 * Count bets for a single-race wager (product of runners per leg)
 * @param {any} runners Runners per leg
 * @param {any} maxCount Max allowed bets (unused)
 * @returns {number} Number of bets
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const countSingle = (runners: any, maxCount?: any) => {
  let count = 1;

  // return 0 if the number of runners is 0
  if (runners.length === 0) return 0;

  // loop through the runners to calculate the number of bets
  for (let leg = 0; leg < runners.length; leg++) {
    count = count * runners[leg].length;
  }

  // return the number of bets
  return count;
};


/**
 * Count unique unordered combos across legs
 * @param {any} runners Runners per leg
 * @param {any} maxCount Max allowed bets
 * @returns {number} Number of unique combos
 */
export const countCombos = (runners: any, maxCount: any) => {
  const allCombos = getCombos(runners, [], 0, {}, maxCount);
  return Object.keys(allCombos).length;
};


/**
 * Recursively build the set of unique combos across legs
 * @param {any} runners Runners per leg
 * @param {any} permSoFar Working permutation
 * @param {any} curIndex Current leg index
 * @param {any} allCombos Combos accumulator (keyed set)
 * @param {any} maxCount Max allowed combos
 * @param {any} unorderedPool True when combos are order-agnostic
 * @param {any} races Number of races (1 by default)
 * @returns {any} Combos accumulator, or null when maxCount exceeded
 */
export const getCombos = (
  runners: any,
  permSoFar: any,
  curIndex: any,
  allCombos: any,
  maxCount: any,
  unorderedPool?: any,
  races?: any
) => {
  // if unorderedPool is null, set it to true
  if(unorderedPool == null) {
    unorderedPool = true;
  }

  // if races is null, set it to 1
  if(races == null) {
    races = 1;
  }

  // if allCombos is null, set it to an empty object
  if (!allCombos) {
    allCombos = {};
  }

  // TODO: need explanation on this
  for (let i = 0; i < runners[curIndex].length; i++) {
    if (permSoFar.indexOf(runners[curIndex][i]) === -1 || races > 1) {
      permSoFar.push(runners[curIndex][i]);
      if (curIndex + 1 != runners.length) {
        allCombos = getCombos(
          runners,
          permSoFar,
          curIndex + 1,
          allCombos,
          maxCount,
          unorderedPool,
          races
        );
        if (allCombos === null) return null;
      } else {
        const clonedPerm = clone(permSoFar);
        if (unorderedPool) {
          clonedPerm.sort();
        }

        let permSoFarString = clonedPerm[0];
        for (let j = 1; j < clonedPerm.length; j++) {
          permSoFarString = permSoFarString + "/" + clonedPerm[j];
        }
        allCombos[permSoFarString] = true;
      }
      permSoFar.pop();
    }
  }

  // return the allCombos
  return allCombos;
};


/**
 * Recursively build all ordered permutations across legs
 * @param {any} runners Runners per leg
 * @param {any} permSoFar Working permutation
 * @param {any} curIndex Current leg index
 * @param {any} allPerms Permutations accumulator
 * @param {any} maxCount Max allowed permutations
 * @returns {any} Permutations accumulator, or null when maxCount exceeded
 */
export const getPerms = (runners: any, permSoFar: any, curIndex: any, allPerms: any, maxCount: any) => {
  // if allPerms is null, set it to an empty array
  if (!allPerms) {
    allPerms = [];
  }

  // loop through the runners
  for (let i = 0; i < runners[curIndex].length; i++) {

    // if the runner is not already in the permSoFar
    if (permSoFar.indexOf(runners[curIndex][i]) === -1) {

      // add the runner to the permSoFar
      permSoFar.push(runners[curIndex][i]);
        if (curIndex + 1 != runners.length) {
          allPerms = getPerms(runners, permSoFar, curIndex + 1, allPerms, maxCount);

          // if allPerms is null, return null
          if (allPerms === null) return null;
        } else {
          const clonedPerm = clone(permSoFar); // needs clone not refernce!
          allPerms.push(clonedPerm);
        }

        // remove the runner from the permSoFar
        permSoFar.pop();
      }
  }

  // return the allPerms
  return allPerms;
};


/**
 * Count ordered permutations across legs (no duplicates within a permutation)
 * @param {any} runners Runners per leg
 * @param {any} permSoFar Working permutation
 * @param {any} curIndex Current leg index
 * @param {any} curCount Running count
 * @param {any} maxCount Max allowed permutations (unused)
 * @returns {number} Number of permutations
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const countPerms = (runners: any, permSoFar: any, curIndex: any, curCount: any, maxCount?: any) => {
  // loop through the runners
  for (let i = 0; i < runners[curIndex].length; i++) {

    // if the runner is not already in the permSoFar
    if (permSoFar.indexOf(runners[curIndex][i]) === -1) {

      // add the runner to the permSoFar
      permSoFar.push(runners[curIndex][i]);

      if (curIndex + 1 !== runners.length) {
        // count the number of permutations
        curCount = countPerms(runners, permSoFar, curIndex + 1, curCount);

        // if curCount is less than 0, return curCount
        if (curCount < 0) return curCount;
      } else {
        curCount++;
      }

      // remove the runner from the permSoFar
      permSoFar.pop();
    }
  }

  // return the curCount
  return curCount;
};


/**
 * Expand a tote compressed selection string (e.g. "1,3-5+7") into a number list
 * @param {any} compressedString Compressed selection
 * @returns {any[]} Expanded number list
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const toteCompressedStringToList = (compressedString: any) => {
  compressedString = compressedString.replace("+",",");
  const numberSplit = compressedString.split(",");
  const convertedNumbers = [];

  // TODO: need explanation on this
  for (let i = 0; i < numberSplit.length; i++) {
    if (!isNaN(numberSplit[i])) {
      convertedNumbers.push(numberSplit[i]);
    } else {
      if (numberSplit[i].indexOf("-") < 0) continue;

      const splitRange = numberSplit[i].split("-");
      if (splitRange.length !== 2) continue;

      for (let j = splitRange[0]; j <= splitRange[1]; j++) {
        convertedNumbers.push(j);
      }
    }
  }
  return convertedNumbers;
}


/**
 * Deep-clone a plain object (skips already-active clones)
 * @param {any} obj Source object
 * @returns {any} Cloned object
 */
export const clone = (obj: any) => {
  // TODO: need explanation on this

  if (obj === null || typeof (obj) !== "object" || "isActiveClone" in obj) return obj;

  let temp;
  if (obj instanceof Date)
    // temp = new obj.constructor();
    temp = new (obj.constructor as any)();
  else
    temp = obj.constructor();

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      obj["isActiveClone"] = null;
      temp[key] = clone(obj[key]);
      delete obj["isActiveClone"];
    }
  }

  return temp;
};
